import React from 'react';
import { useSelector, shallowEqual } from "react-redux";
import { RootState } from "../services/rootReducer";
import { selectViewType } from "../services/View_Slice";
import { format, parseISO } from 'date-fns';
import EventIcon from '@material-ui/icons/Event';
import styles from "./css/LatestEntryInfo.module.css";

/**
 * LatestEntryInfo Component
 *
 * Displays an info bar showing the date of the most recent entry
 * after auto-navigation from facet filter application.
 *
 * Format: "Latest report availability: 15 Mar 2024 for CryoSat-2, GOM"
 *
 * Only visible when:
 * - Facet filters have been applied AND
 * - Auto-navigation to latest date has occurred
 * - NOT in list view with active search (search results don't use calendar dates)
 */
const LatestEntryInfo: React.FC = () => {
    const latestDateInfo = useSelector((state: RootState) => state.FacetNavigation.latestDateInfo);
    const viewType = useSelector(selectViewType);
    const mobile = useSelector((state: RootState) => state.View.mobile);
    const freeTextSearch = useSelector((state: RootState) => state.Search.freeTextSearch);

    // Don't render if no navigation has occurred
    if (!latestDateInfo) {
        return null;
    }

    // Hide in list view when search is active (search results aren't date-filtered)
    const isListView = viewType === 0;
    const hasActiveSearch = freeTextSearch && freeTextSearch.trim().length > 0;
    if (isListView && hasActiveSearch) {
        return null;
    }

    // Format the date nicely (e.g., "15 Mar 2024")
    let formattedDate: string;
    try {
        const dateObj = parseISO(latestDateInfo.date);
        formattedDate = format(dateObj, 'd MMM yyyy');
    } catch {
        formattedDate = latestDateInfo.date;
    }

    // Extract filter values from "value@facetName" format
    const filterValues = latestDateInfo.filters.map(filter => {
        const parts = filter.split('@');
        return parts.length > 0 ? parts[0] : filter;
    });

    // Join filter values with comma
    const filterList = filterValues.join(', ');

    const containerClass = mobile ? styles.container_mobile : styles.container;

    return (
        <div className={containerClass}>
            <EventIcon className={styles.icon} />
            <span className={styles.text}>
                Latest report availability: <strong>{formattedDate}</strong>
                {filterList && (
                    <> for <span className={styles.filterList}>{filterList}</span></>
                )}
            </span>
        </div>
    );
};

export default React.memo(LatestEntryInfo);
